import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, MessageCircle, Building2, MapPin, BarChart3, Plug, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import DemoRequestModal from '../components/DemoRequestModal';

const BENEFITS = [
  { icon: Building2, title: 'Perfil corporativo profesional', desc: 'Logo, portada, descripción, equipo y propiedades en una sola URL pública e indexable en Google.' },
  { icon: MapPin, title: 'Cobertura nacional', desc: 'Llega a compradores en los 1.122 municipios de Colombia. Mapa interactivo estilo Airbnb.' },
  { icon: BarChart3, title: 'Estadísticas en tiempo real', desc: 'Visualiza contactos, clics en WhatsApp, vistas y desempeño del equipo desde un dashboard claro.' },
  { icon: Plug, title: 'Integraciones CRM (próximamente)', desc: 'Tu inventario en Habita se sincronizará automáticamente con Wasi, Tokko, Inmovilla y más.' },
  { icon: MessageCircle, title: 'Contacto directo por WhatsApp', desc: 'Tus clientes te escriben directo sin intermediarios ni comisiones por lead.' },
  { icon: Sparkles, title: 'Beta gratuita', desc: 'Sin pagos, sin permanencia. Pruébalo todo y crece con nosotros.' },
];

const FEATURES = [
  'Equipo de asesores con perfiles individuales',
  'Sello "Inmobiliaria Verificada"',
  'URL personalizada para compartir',
  'Open Graph para WhatsApp y redes',
  'Branding propio (logo + portada + colores)',
  'Importación masiva (próximamente)',
];

export default function ForAgenciesPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <div className="min-h-screen bg-habita-cream">
      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <SEO
        title="Para inmobiliarias y constructoras · habita.co"
        description="Habita es la plataforma inmobiliaria de Colombia diseñada para que las inmobiliarias tengan presencia digital, equipo, inventario y estadísticas en un solo lugar."
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-habita-dark via-habita to-habita opacity-95" />
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 py-20 lg:py-28 text-white">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-bold uppercase tracking-widest">Beta gratuita</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 leading-tight">
            Una vitrina digital completa<br/>para tu inmobiliaria.
          </h1>
          <p className="text-lg lg:text-xl text-white/80 mt-5 max-w-2xl">
            Habita no es solo un portal. Es tu marca, tu equipo y tu inventario en una sola plataforma — diseñada para Colombia.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button onClick={() => setDemoOpen(true)} data-testid="for-agencies-demo-cta" className="bg-amber-500 hover:bg-amber-600 text-habita-dark font-bold h-12 px-8 rounded-full text-base">
              Solicitar una demostración
            </Button>
            <Link to="/registro" data-testid="for-agencies-register-cta">
              <Button variant="outline" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 h-12 px-8 rounded-full">
                Crear mi inmobiliaria gratis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark text-center">Más que un portal de propiedades</h2>
        <p className="text-gray-600 text-center mt-3 max-w-2xl mx-auto">Habita está diseñado para que las inmobiliarias gestionen su marca, su equipo y su inventario desde un solo lugar.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {BENEFITS.map(b => (
            <div key={b.title} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow" data-testid={`benefit-${b.title.split(' ')[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-habita-mint flex items-center justify-center mb-4">
                <b.icon className="w-5 h-5 text-habita" />
              </div>
              <h3 className="font-serif font-bold text-habita-dark">{b.title}</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES checklist */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">Todo lo que necesitas, en un solo lugar</h2>
            <p className="text-gray-600 mt-4">Cada inmobiliaria en Habita obtiene su propia página corporativa, lista para compartir, indexable en Google y optimizada para conversión.</p>
            <ul className="mt-6 space-y-3">
              {FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-habita-mint flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-habita" strokeWidth={3} /></div>
                  <span className="text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-habita-cream rounded-3xl p-8 lg:p-10">
            <p className="text-xs uppercase tracking-widest text-habita font-bold">EJEMPLO DE PERFIL</p>
            <div className="bg-white rounded-2xl mt-4 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-habita flex items-center justify-center text-white text-xl font-bold">IN</div>
                <div>
                  <p className="font-serif font-bold text-habita-dark text-lg">Inmobiliaria Norte</p>
                  <p className="text-xs text-gray-500">Bogotá · Desde 2010 · <span className="text-emerald-600 font-semibold">VERIFICADA</span></p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-5 text-center text-xs">
                <div className="bg-habita-cream rounded-lg p-2"><p className="font-bold text-habita-dark text-lg">87</p><p className="text-gray-500">Propiedades</p></div>
                <div className="bg-habita-cream rounded-lg p-2"><p className="font-bold text-habita-dark text-lg">12</p><p className="text-gray-500">Asesores</p></div>
                <div className="bg-habita-cream rounded-lg p-2"><p className="font-bold text-habita-dark text-lg">14</p><p className="text-gray-500">Años</p></div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-5">gethabita.co/inmobiliaria/inmobiliaria-norte</p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-20 text-center">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">¿Listo para unir tu inmobiliaria?</h2>
        <p className="text-gray-600 mt-4">Únete a la beta gratuita. Sin tarjetas, sin permanencia, sin sorpresas.</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
          <Button onClick={() => setDemoOpen(true)} className="btn-habita h-12 px-8 rounded-full text-base" data-testid="for-agencies-bottom-demo-cta">Solicitar una demostración</Button>
          <Link to="/registro" data-testid="for-agencies-bottom-cta"><Button variant="outline" className="h-12 px-8 rounded-full">Crear cuenta gratis</Button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
